/**
 * Turning stage indices into what a fleet operator reads.
 *
 * The phone reports two of them and they answer different questions.
 * `reported_stage_index` is gated on capture: it names the stage of a session
 * actually running. `reported_target_stage_index` is not: it names the stage a
 * `phone.start_recording` carrying no `stage_index` would bind to, which is
 * null on a phone whose driver has picked nothing — the default after every
 * geofence change, and the state where such a start is refused.
 */

import { stageLabel, type StageType } from '@/pages/admin/stages/stageModel';
import type { FleetCommand, FleetDevice } from '@/types';

export type TargetStageReadout =
  /** A start naming no stage binds here. */
  | { kind: 'targeted'; index: number }
  /** No stage is targeted: a start must name one or it will be refused. */
  | { kind: 'untargeted' }
  /** Nothing worth claiming — say nothing rather than guess. */
  | { kind: 'unknown' };

export function targetStageReadout(device: FleetDevice): TargetStageReadout {
  if (device.reported_target_stage_index != null) {
    return { kind: 'targeted', index: device.reported_target_stage_index };
  }
  // An offline device's snapshot is whatever it last managed to send, so a
  // missing target there is as likely to be stale as true. A recording device
  // is not asking this question at all — its live stage is the answer. Both
  // would otherwise raise a warning the operator can neither trust nor act on.
  if (!device.online || device.reported_stage_index != null) return { kind: 'unknown' };
  return { kind: 'untargeted' };
}

/**
 * What a `phone.start_recording` asked for, for the command history's detail
 * column.
 *
 * The stage half is a diagnosis an operator cannot get anywhere else. A start
 * that named no stage and one that named an unusable stage both fail, and the
 * refusal text does not always separate them — `No eligible stage is targeted`
 * never says whether the command tried. Only the payload does.
 */
export function startCommandDetail(cmd: FleetCommand, stageType: StageType): string {
  const stage = cmd.payload?.stage_index;
  // A bool is not an index, and neither is a string; either means the payload
  // did not name a stage, whatever it meant to.
  const named =
    typeof stage === 'number' && Number.isInteger(stage) && stage >= 0
      ? stageLabel(stage, stageType)
      : "driver's stage";
  const sessionId = cmd.payload?.session_id ?? cmd.result?.session_id;
  return sessionId ? `${named} · ${String(sessionId).slice(0, 40)}` : named;
}
